import { Reducer, Store as ReduxStore } from 'redux';
import {
    AccountingState,
    AccountNote,
    AccountType,
    AgeCategoryClassification,
    AvailabilityAdjustment,
    OrderItemType,
    PageSize,
    PaxCount,
    PaymentCardFormat,
    PaymentCardOrigin,
    PaymentCardState,
    Payment as PaymentDto,
    PaymentProviderUsageMethod,
    PaymentState,
    PaymentType,
    ProductChargingMode,
    ProductConsumptionMoment,
    ProductPostingMode,
    ProductPricing,
    AmountPart as ProductPricingTarget,
    ProductRuleAction,
    ResourceBlockType,
    ResourceCategoryType,
    ServiceOrderNoteType,
    ServiceOrderOrigin,
    ServiceOrderState,
    VisitOptions,
} from '@mews-commander/data-access';
import { SearchType, TextKey } from '@mews-commander/enums';
import { PaymentCardType, ProductRuleCondition, ServiceType, TimeUnit } from '@mews/enums';
import { LocalizedPropertyObject, SymbolPosition, SymbolSpacing } from '@mews/framework';
import { RoutingHistoryState } from '@mews/routing';
import { CoproductElement, Identifiable, Optional, Unit } from '@mews/std';
import { Amount, ProfileData } from './entities';
import { WithAccountId, WithEnterpriseId } from './props';
import { CommanderThunkDispatch } from './thunk';
import './staticDataRouteOption';

export * from './entities';
export * from './props';
export * from './thunk';

export interface SimpleUser extends Identifiable {
    languageCode: string;
    email: string;
    firstName: string;
    lastName: string;
    secondLastName: string;
    imageId: string;
    createdUtc: string;
}

export interface CustomerUser extends Identifiable {
    nativeLanguageCode: string | null;
    preferredLanguageCode: string | null;
    preferredCultureCode: string | null;
}

export interface Profile extends Identifiable {
    name: string;
    isActive: boolean;
    data: ProfileData;
}

export interface ApplicationState {
    [key: string]: {};
    navigation: RoutingHistoryState;
}

export interface Store extends ReduxStore<ApplicationState> {
    getState: () => ApplicationState;
    dispatch: CommanderThunkDispatch;
    injectReducer: (key: string, reducer: Reducer) => void;
}

export interface CreditLimit extends Identifiable, WithEnterpriseId, WithAccountId {
    amount: SimpleAmount | null;
}

export interface Option<S = string> {
    value: S | null;
    label: React.ReactNode;
    disabled: boolean;
    secondaryText: React.ReactNode;
    indent: boolean;
    inactive: boolean;
    searchValue?: string;
}

export interface GroupOption<S = string> {
    groupLabel: React.ReactNode;
    groupOptions: Array<Option<S>>;
    showGroupDivider?: boolean;
}

export type LocalizedOptions = Record<string, Array<Option>>;

export interface Device extends Identifiable {
    name: string;
}

export interface MenuItemOption extends Omit<Option, 'value'> {
    value?: string | number;
}

export enum PreauthorizationState {
    Pending = 'Pending',
    Preauthorized = 'Preauthorized',
    Canceled = 'Canceled',
    Charged = 'Charged',
    Failed = 'Failed',
    Verifying = 'Verifying',
}

export interface ServiceOptions {
    hasConfigurableBusinessSegment: boolean;
    orderRequiresSegment: boolean;
    hasOverridablePrice: boolean;
}

export type ReservableServiceCoProduct = CoproductElement<ServiceType.Bookable, ReservableServiceValue>;
export type OrderableServiceCoProduct = CoproductElement<ServiceType.Additional, Unit>;

export interface Service {
    id: string;
    isActive: boolean;
    enterpriseId: string;
    ordering: number;
    createdUtc: string;
    cityTaxCode: string;
    names: Record<string, string>;
    shortNames: Record<string, string>;
    defaultBusinessSegmentId: string | null;
    options: ServiceOptions;
    data: ReservableServiceCoProduct | OrderableServiceCoProduct;
}

export interface ReservableServiceValue {
    departureTime: string;
    arrivalTime: string;
    timeUnit: TimeUnit;
    startOffset: string;
    endOffset: string;
    visitOptions: VisitOptions;
}

export interface SimpleDataReservation {
    accountId: string;
    adultCount: number;
    assignedResourceId: string | null;
    assignedResourceLocked: boolean;
    assignedSlotId: string | null;
    childCount: number;
    endUtc: string;
    groupId: string;
    id: string;
    occupancyData: Array<OccupancyData>;
    options: ReservationOptions;
    releasedUtc: string | null;
    requestedCategoryId: string;
    serviceId: string;
    serviceOrderState: ServiceOrderState;
    startUtc: string;
    startedUtc: string;
}

export interface ResourceBlock {
    assignedResourceId: string;
    endUtc: string;
    id: string;
    isActive: boolean;
    name?: string | null;
    notes?: string | null;
    startUtc: string;
    type: ResourceBlockType;
}

export interface ProductServiceOrder {
    id: string;
    serviceId: string;
    /**  @deprecated Use ownerId instead */
    accountId?: string | null;
    /**  @deprecated Use ownerId instead */
    customerId?: string | null;
    ownerId: string;
    creatorProfileId: string;
    number: string;
    state: ServiceOrderState;
    origin: ServiceOrderOrigin;
    createdUtc: string;
    startUtc: string;
    updatedUtc: string;
    canceledUtc: string | null;
    businessSegmentId?: string | null;
    totalCost: Amount;
}

export interface ProductPricingRelativeValue {
    productIds: string[];
    taxRateCodes: string[];
    multiplier: number;
    target: ProductPricingTarget;
}

export type ProductPricingRelativeCoProduct = CoproductElement<ProductPricing.Relative, ProductPricingRelativeValue>;
export type ProductPricingAbsoluteCoProduct = CoproductElement<ProductPricing.Absolute, Amount>;

export type ProductPricingCoProducts = ProductPricingRelativeCoProduct | ProductPricingAbsoluteCoProduct;

export interface ProductOptions {
    selectedByDefault: boolean;
    billAsPackage: boolean;
    offerToCustomer: boolean;
    excludePriceFromOffer: boolean;
    offerToEmployee: boolean;
    createTask: boolean;
    proRataCharging: boolean;
}

export interface ProductPromotions {
    beforeCheckIn: boolean;
    afterCheckIn: boolean;
    duringStay: boolean;
    beforeCheckOut: boolean;
    afterCheckOut: boolean;
    duringCheckOut: boolean;
}

export interface ProductClassifications {
    food: boolean;
    beverage: boolean;
    wellness: boolean;
    cityTax: boolean;
}

export interface Product {
    id: string;
    serviceId: string;
    productCategoryId: string | null;
    accountingCategoryId: string | null;
    rebateAccountingCategoryId: string | null;
    canceledItemAccountingCategoryId: string | null;
    names: LocalizedPropertyObject;
    units: LocalizedPropertyObject;
    shortNames: LocalizedPropertyObject;
    externalNames: LocalizedPropertyObject;
    descriptions: LocalizedPropertyObject;
    options: ProductOptions;
    promotions: ProductPromotions;
    classifications: ProductClassifications;
    chargingMode: ProductChargingMode;
    postingMode: ProductPostingMode;
    ordering: number;
    pricing: ProductPricingCoProducts;
    isDefault: boolean;
    isDeleted: boolean;
    imageId: string | null;
    creatorProfileId: string;
    updaterProfileId: string;
    createdUtc: string;
    updatedUtc: string;
    consumptionMoment: ProductConsumptionMoment;
    externalIdentifier?: string | null;
    taxExemptionReasonCode?: string | null;
    taxExemptionLegalReference?: string | null;
}

export interface AbsolutePricingProduct extends Product {
    pricing: ProductPricingAbsoluteCoProduct;
}

export interface NullableUpdateApiValue<T> {
    value: T | null;
}

export interface UpdateApiValue<T> {
    value: T;
}

export interface ReservationOptions {
    allCompanionsCheckedIn: boolean;
    anyCompanionCheckedIn: boolean;
    ownerCheckedIn: boolean;
    ownerCheckedOut: boolean;
    skipCleaning: boolean;
}

export interface SearchResult {
    title?: string;
    description?: string;
    url?: string;
    searchType: SearchType;
    id: string;
}

export interface ServiceOrderNote {
    text: string;
    id: string;
    serviceOrderId: string;
    createdUtc: string;
    type: ServiceOrderNoteType;
}

export interface ProductOrderValue {
    ageCategoryId: string | null;
    productId: string | null;
}

interface RebateValue {
    rebatedItemId: string;
    originalItemId: string;
    rebateIndex: number;
    rebateReason: string | null;
}

export type RebateCoProducts =
    | CoproductElement<OrderItemType.AdditionalExpenseRebate, RebateValue>
    | CoproductElement<OrderItemType.NightRebate, RebateValue>
    | CoproductElement<OrderItemType.ProductOrderRebate, RebateValue>;

export type ProductOrderCoProduct = CoproductElement<OrderItemType.ProductOrder, ProductOrderValue>;

export interface DepositValue {
    pastDepositId?: string;
}

export type OrderItemCoProducts =
    | RebateCoProducts
    | ProductOrderCoProduct
    | CoproductElement<OrderItemType.SpaceOrder, null>
    | CoproductElement<OrderItemType.CancellationFee, null>
    | CoproductElement<OrderItemType.CityTax, null>
    | CoproductElement<OrderItemType.CityTaxDiscount, null>
    | CoproductElement<OrderItemType.CustomItem, null>
    | CoproductElement<OrderItemType.Deposit, DepositValue>
    | CoproductElement<OrderItemType.ExchangeRateDifference, null>
    | CoproductElement<OrderItemType.Surcharge, null>
    | CoproductElement<OrderItemType.MulticurrencyFee, null>
    | CoproductElement<OrderItemType.TaxCorrection, null>
    | CoproductElement<OrderItemType.InvoiceFee, null>;

export interface OrderItem {
    accountingState: AccountingState;
    billId: string | null;
    consumedUtc: string;
    createdUtc: string;
    updatedUtc: string;
    canceledUtc?: string | null;
    creatorProfileId: string;
    updaterProfileId: string;
    id: string;
    /** @deprecated Temporarily not using this due to the company billing migration. */
    isCanceled: boolean;
    isDeleted: boolean;
    nightMultiplier: number;
    notes: string;
    ownerId: string;
    personMultiplier: number;
    productChargingMode: ProductChargingMode;
    /**  @deprecated Use productId ProductOrderCoProduct's value instead */
    productId: string | null;
    proRataStartUtc?: string | null;
    proRataEndUtc?: string | null;
    /**  @deprecated Use rebatedItemId RebateCoProducts' value instead */
    rebatedOrderItemId: string | null;
    isRebated: boolean;
    isReinstated: boolean;
    serviceOrderId: string;
    startUtc: string | null;
    timeMultiplier: number;
    totalAmount: Amount;
    fullTimeUnitTotalAmount: Amount;
    /**  @deprecated Use data.discriminator instead */
    type: OrderItemType;
    unitAmount: Amount;
    unitMultiplier: number;
    displayGrouped?: boolean | null;
    assignedAccountingCategoryId?: string | null;
    data: OrderItemCoProducts;
}

export interface ProductOrderItem extends Omit<OrderItem, 'data'> {
    data: ProductOrderCoProduct;
}

declare global {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    interface CommanderContainer {}
}

export type GetState = () => ApplicationState;

export interface CustomerBalance {
    customerId: string;
    balance: Amount;
    openItemCount: number;
}

export interface Payment {
    ownerId: string;
    originalPayerId: string;
    billId: string;
    serviceOrderId: string | null;
    cashierId: string;
    createdUtc: string;
    creatorProfileId: string;
    currency: string;
    amount: number;
    notes: string | null;
    id: string;
    name: string;
    state: PaymentState;
    type: PaymentType;
    value: Amount;
    data: PaymentDto['data'];
    identifier: string;
}

export interface CustomerClassifications {
    paymasterAccount: boolean;
    blacklist: boolean;
    media: boolean;
    loyaltyProgram: boolean;
    previousComplaint: boolean;
    returning: boolean;
    staff: boolean;
    friendOrFamily: boolean;
    topManagement: boolean;
    important: boolean;
    veryImportant: boolean;
    problematic: boolean;
    cashlist: boolean;
    disabledPerson: boolean;
    military: boolean;
    airline: boolean;
    healthCompliant: boolean;
}

export interface PreferredResourceFeatures {
    seaView: boolean;
    riverView: boolean;
    oceanView: boolean;
    twinBeds: boolean;
    doubleBed: boolean;
    rollawayBed: boolean;
    upperBed: boolean;
    lowerBed: boolean;
    balcony: boolean;
    accessibleBathroom: boolean;
    accessibleRoom: boolean;
    elevatorAccess: boolean;
    highFloor: boolean;
    kitchenette: boolean;
    airConditioning: boolean;
    privateJacuzzi: boolean;
    privateSauna: boolean;
    ensuiteRoom: boolean;
    privateBathroom: boolean;
    sharedBathroom: boolean;
}

export interface CustomerOptions {
    invoiceable: boolean;
    sendMarketingEmails: boolean;
    billAddressObjection: boolean;
}

export interface Customer {
    id: string;
    userId: string | null;
    number: number;
    profileId: string | null;
    firstName: string | null;
    lastName: string | null;
    secondLastName: string | null;
    birthPlace: string | null;
    telephone: string | null;
    occupation: string | null;
    carRegistrationNumber: string | null;
    taxIdentifier: string | null;
    accountingCode: string | null;
    billingCode: string | null;
    loyaltyCode: string | null;
    notes: string | null;
    email: string | null;
    nationalityCode: string | null;
    companyId: string | null;
    birthDate: string | null;
    titlePrefix: number | null;
    title: string | null;
    gender: number | null;
    sex: number | null;
    customerSex: string | null;
    preferredLanguageCode: string | null;
    nativeLanguageCode: string | null;
    imageId: string | null;
    classifications: number | null;
    customerClassifications: CustomerClassifications;
    preferredResourceFeatures: PreferredResourceFeatures;
    options: CustomerOptions;
}

export interface Companionship {
    id: string;
    reservationId: string;
    companionId: string;
    groupId: string;
}

export interface PaymentCard {
    accountId: string;
    createdUtc: string;
    creatorProfileId: string;
    expiration: string | null;
    format: PaymentCardFormat;
    holderName: string | null;
    id: string;
    isActive: boolean;
    isChargeable: boolean;
    isGatewayOperationAllowed: boolean;
    isGatewayProvider: boolean;
    issuingBank: string;
    issuingCountryCode: string;
    kind: PaymentProviderUsageMethod;
    lastDigits: string;
    notes: string | null;
    obfuscatedNumber: string;
    origin: PaymentCardOrigin;
    state: PaymentCardState;
    storageId: string;
    type: PaymentCardType;
}

export interface AccountingCategory {
    id: string;
    isActive: boolean;
    name: string;
    code: string;
    externalCode: string;
    ledgerAccountCode: string;
    postingAccountCode: string;
    costCenterCode: string;
    classification: string;
    itemsNotTaxable: boolean;
}

export interface Language {
    code: string;
    localName: string;
}
export interface ProductRulesRequest {
    productRuleIds: string[];
    businessSegmentIds: string[];
    rateIds: string[];
    rateGroupIds: string[];
    resourceCategoryIds: string[];
    resourceCategoryTypes: string[];
    origins: string;
    extent: {
        ruleActions: boolean;
    };
}
export interface ProductRule {
    id: string;
    priority: number;
    serviceId: string;
    businessSegmentId: string;
    businessSegmentCondition: ProductRuleCondition | null;
    rateId: string;
    rateCondition: ProductRuleCondition | null;
    rateGroupId: string;
    rateGroupCondition: ProductRuleCondition | null;
    resourceCategoryId: string;
    resourceCategoryCondition: ProductRuleCondition | null;
    resourceCategoryType: ResourceCategoryType | null;
    resourceCategoryTypeCondition: ProductRuleCondition | null;
    origin: ServiceOrderOrigin | null;
    originCondition: ProductRuleCondition | null;
    minimumTimeUnitCount: number;
    maximumTimeUnitCount: number;
}

export interface RuleAction {
    id: string;
    ruleId: string;
    productId: string;
    action: ProductRuleAction;
}

export interface ResourceCategoryAvailability {
    timeUnitBaseStartUtc: string;
    resourceCategoryId: string;
    value: number;
    defaultPrice?: Amount | null;
    publicAdjustmentValue: number;
    remainingBlockAdjustmentValue: number;
    blockAdjustmentValue: number;
    publicAdjustment: AvailabilityAdjustment | null;
    blockAdjustmentData: Array<BlockAdjustmentData>;
}

export interface ResourceFeatureAssignment extends Identifiable {
    resourceId: string;
    featureId: string;
}

export interface BlockAdjustmentData {
    availabilityBlockId: string;
    blockAvailability: number;
    blockPickup: number;
    remainingBlockAvailability: number;
    availabilityBlockAdjustment: AvailabilityAdjustment;
    paxCounts?: PaxCount[] | null;
}

export interface FileUpdateValue {
    name: string;
    type: string;
    data?: string;
}

export interface LegalEnvironment {
    allowProductClassifications: boolean;
    code: string;
    countryCode: string;
    englishName: string;
    validityStartUtc: string | null;
    validityEndUtc: string | null;
}

export interface BookingEngine {
    id: string;
    name: string;
    serviceId: string;
}

export interface RegistrationCardConfiguration {
    id: string;
    serviceId: string;
    format: PageSize;
}

export interface AgeCategory {
    id: string;
    serviceId: string;
    classification: AgeCategoryClassification;
    name: LocalizedPropertyObject;
    shortName: LocalizedPropertyObject;
    minimalAge?: number | null;
    maximalAge?: number | null;
    isDefault: boolean;
    isDeleted: boolean;
}

/**
 * @deprecated use generated one
 */
export interface OccupancyData {
    ageCategoryId: string;
    personCount: number;
}

export interface Place {
    coordinates: {
        latitude: number | null;
        longitude: number | null;
    };
    country: {
        alpha3Code: string | null;
        code: string | null;
        englishName: string | null;
        sovereignCountryCode: string | null;
    };
    countrySecondarySubdivision: string | null;
    countrySubDivisionName: string | null;
    countrySubdivision: string | null;
    countryTertiarySubdivision: string | null;
    extendedPostalCode: string | null;
    freeformAddress: string | null;
    id: string;
    municipality: string | null;
    municipalitySubdivision: string | null;
    postalCode: string | null;
    streetName: string | null;
    streetNumber: string | null;
}

export interface AccountsLegalEntityIdentifier {
    accountId: string;
    legalEntityIdentifierCode: string;
    value: string | null;
}

export interface IdentifierConfiguration {
    accountingEnvironmentCode: string;
    legalEntityIdentifierCode: string;
    isRequired: boolean;
    code: string;
}

export interface LegalEntityIdentifier {
    code: string;
    label: string;
    validationRegex: string | null;
}

export interface Source {
    id: string;
    code: Optional<number>;
    name: Optional<string>;
    type: Optional<string>;
}

export interface DefaultAmount {
    amount: number;
    currencyCode: string;
}

export interface SimpleAmount {
    value: number;
    currencyCode: string;
}

/**  @deprecated Temporary type */
export interface Room {
    id: string;
    description: string;
}

export interface Interval {
    startUtc: string;
    endUtc: string;
}

export interface Settlement {
    id: string;
    reservationId: string;
    reservationNumber: string;
    action: 'Payment' | 'Preauthorization';
    scheduledUtc: string;
    accountId: string;
    enterpriseId: string;
    rule: string;
}

export interface Preauthorization {
    accountId: string;
    amount: number;
    code: string | null;
    createdUtc: string;
    creatorProfileId: string;
    currency: string;
    expiration: string;
    expirationUtc: string;
    id: string;
    identifier: string;
    notes: string | null;
    paymentCardId: string;
    sequenceCode: string | null;
    state: PreauthorizationState;
    usageMethod: PaymentProviderUsageMethod;
}

interface CultureFormat {
    code: string;
    symbol: string;
    symbolPosition: SymbolPosition;
    symbolSpacing: SymbolSpacing;
}

export interface Currency extends CultureFormat {
    id: string;
    precision: number;
}

interface CustomerAccountNote extends AccountNote {
    data: AccountNote['data'] & { discriminator: AccountType.Customer };
}
interface CompanyAccountNote extends AccountNote {
    data: AccountNote['data'] & { discriminator: AccountType.Company };
}

export const isCustomerAccountNote = (note: AccountNote): note is CustomerAccountNote =>
    note.data.discriminator === AccountType.Customer;

export const isCompanyAccountNote = (note: AccountNote): note is CompanyAccountNote =>
    note.data.discriminator === AccountType.Company;

export type BookablePeriodTextKey = TextKey.Day | TextKey.Hour | TextKey.Month | TextKey.Night;
